import { useState } from 'react';

import PropTypes from 'prop-types';

import { useButtonPressedCallback } from '@zenbusiness/application-commons-clickstream';
import { useIntl } from '@zenbusiness/application-commons-intl';
import BasicDialog from '@zenbusiness/zui/BasicDialog';
import { useTheme } from '@zenbusiness/zui/styles';
import Text from '@zenbusiness/zui/Text';
import useMediaQuery from '@zenbusiness/zui/useMediaQuery';

import useDisableMfa from '#client/hooks/useDisableMfa';

import { STEP_UP_REQUIRED } from '../TwoStepVerification/constants';

export const LOCALIZED_TEXT = {
  title: 'dashboard.accountSettings.turnOffMfa.title',
  body: 'dashboard.accountSettings.turnOffMfa.body',
  confirm: 'dashboard.accountSettings.turnOffMfa.confirm',
  cancel: 'dashboard.accountSettings.turnOffMfa.cancel',
  mfaRequired: 'dashboard.accountSettings.turnOffMfa.mfaRequired',
  failed: 'dashboard.accountSettings.turnOffMfa.failed',
};

const TurnOffMfaDialog = ({ isOpen, onClose, onTurnedOff, onStepUpRequired }) => {
  const { formatMessage: fm } = useIntl();
  const theme = useTheme();
  const fullScreen = useMediaQuery(theme.breakpoints.down('sm'));
  const { disableMfa, loading } = useDisableMfa();
  const [errorId, setErrorId] = useState(null);

  const handleClosePressed = useButtonPressedCallback({
    appSection: 'account',
    elementName: 'turn_off_mfa_close',
    elementStyle: 'icon',
  });

  const handleClose = () => {
    setErrorId(null);
    onClose();
  };

  const handleConfirm = async () => {
    setErrorId(null);
    const result = await disableMfa();

    if (result?.disabled) {
      onTurnedOff();
      return;
    }

    if (result?.errorCode === STEP_UP_REQUIRED) {
      onStepUpRequired();
      return;
    }

    setErrorId(result?.errorCode === 'mfaRequired' ? LOCALIZED_TEXT.mfaRequired : LOCALIZED_TEXT.failed);
  };

  return (
    <BasicDialog
      size="sm"
      title={fm({ id: LOCALIZED_TEXT.title })}
      primaryAction={{
        label: fm({ id: LOCALIZED_TEXT.confirm }),
        color: 'primary',
        clickstreamProps: {
          appSection: 'account',
          elementName: 'turn_off_mfa_confirm',
        },
        loading,
        onClick: handleConfirm,
      }}
      secondaryAction={{
        label: fm({ id: LOCALIZED_TEXT.cancel }),
        clickstreamProps: {
          appSection: 'account',
          elementName: 'turn_off_mfa_cancel',
        },
        onClick: handleClose,
      }}
      onClose={() => {
        handleClose();
        handleClosePressed();
      }}
      open={isOpen}
      fullScreen={fullScreen}
    >
      <Text size="md" color="primary">
        {fm({ id: LOCALIZED_TEXT.body })}
      </Text>
      {errorId && (
        <Text size="sm" color="red">
          {fm({ id: errorId })}
        </Text>
      )}
    </BasicDialog>
  );
};

TurnOffMfaDialog.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  onTurnedOff: PropTypes.func.isRequired,
  // Called when customers-api wants a fresh MFA challenge before turning it off.
  onStepUpRequired: PropTypes.func.isRequired,
};

export default TurnOffMfaDialog;
